// Persistent counters that drive rare skin unlocks + wager tracking.
// Two things are tracked:
//   • buggy_survivals — cumulative count of runs won on beach where the boss
//     buggy spawned. 3+ unlocks the "Buggy Slayer" skin.
//   • weather_wins    — per-weather-type win counts. 5 wins in any harsh
//     weather (rain/fog/wind) unlocks the "Storm Rider" skin.
// The wager ledger is server-authoritative — we only cache the last few
// entries locally for optimistic UI.

const BUGGY_KEY = "ppe_buggy_survivals_v1";
const WEATHER_KEY = "ppe_weather_wins_v1";

export function getBuggySurvivals() {
  return Number(localStorage.getItem(BUGGY_KEY) || 0) || 0;
}

export function bumpBuggySurvival() {
  const n = getBuggySurvivals() + 1;
  localStorage.setItem(BUGGY_KEY, String(n));
  return n;
}

export function getWeatherWins() {
  try {
    return JSON.parse(localStorage.getItem(WEATHER_KEY) || "{}") || {};
  } catch {
    return {};
  }
}

export function bumpWeatherWin(type) {
  if (!type || type === "off") return getWeatherWins();
  const wins = getWeatherWins();
  wins[type] = (wins[type] || 0) + 1;
  localStorage.setItem(WEATHER_KEY, JSON.stringify(wins));
  return wins;
}

// Storm Rider unlocks when ANY harsh weather has ≥ 5 wins.
export function hasStormRider() {
  const w = getWeatherWins();
  return ["rain", "fog", "wind"].some((k) => (w[k] || 0) >= 5);
}

// -----------------------------------------------------------------------------
// Wager refund streak — after 2 losing wagers in a row, the next wager is
// comped (chips are refunded to the balance on loss instead of forfeit).
// Wins reset the streak. This gives casual players a soft floor.
const WAGER_LOSS_KEY = "ppe_wager_loss_streak_v1";
const WAGER_COMP_USED_KEY = "ppe_wager_comp_used_v1";

export function getLossStreak() {
  return Number(localStorage.getItem(WAGER_LOSS_KEY) || 0) || 0;
}

export function bumpLossStreak() {
  const n = getLossStreak() + 1;
  localStorage.setItem(WAGER_LOSS_KEY, String(n));
  return n;
}

export function resetLossStreak() {
  localStorage.setItem(WAGER_LOSS_KEY, "0");
  localStorage.setItem(WAGER_COMP_USED_KEY, "0");
}

/** True when the next wager should be comped (refunded on loss). */
export function isWagerCompNext() {
  return getLossStreak() >= 2 && localStorage.getItem(WAGER_COMP_USED_KEY) !== "1";
}

export function markWagerCompUsed() {
  localStorage.setItem(WAGER_COMP_USED_KEY, "1");
}

// -----------------------------------------------------------------------------
// Roulette streak — chain 3 roulette wins in a row for a 5× payout on the 4th
const ROULETTE_STREAK_KEY = "ppe_roulette_win_streak_v1";
const ROULETTE_5X_PENDING_KEY = "ppe_roulette_5x_pending_v1";

export function getRouletteStreak() {
  return Number(localStorage.getItem(ROULETTE_STREAK_KEY) || 0) || 0;
}

/** Called after a roulette-tagged run finishes. Returns new streak count. */
export function bumpRouletteResult(won) {
  if (won) {
    const n = getRouletteStreak() + 1;
    localStorage.setItem(ROULETTE_STREAK_KEY, String(n));
    if (n >= 3) localStorage.setItem(ROULETTE_5X_PENDING_KEY, "1");
    return n;
  }
  localStorage.setItem(ROULETTE_STREAK_KEY, "0");
  localStorage.removeItem(ROULETTE_5X_PENDING_KEY);
  return 0;
}

/** True when the NEXT roulette should award 5× (consumed on any roulette run). */
export function isRoulette5xPending() {
  return localStorage.getItem(ROULETTE_5X_PENDING_KEY) === "1";
}

export function consumeRoulette5x() {
  localStorage.removeItem(ROULETTE_5X_PENDING_KEY);
}

// -----------------------------------------------------------------------------
// Handle change cooldown — 30 seconds between changes
const HANDLE_LAST_KEY = "ppe_handle_last_change_v1";
const HANDLE_COOLDOWN_MS = 30 * 1000;

export function getHandleCooldownRemainingMs() {
  const last = Number(localStorage.getItem(HANDLE_LAST_KEY) || 0) || 0;
  if (!last) return 0;
  return Math.max(0, HANDLE_COOLDOWN_MS - (Date.now() - last));
}

export function markHandleChange() {
  localStorage.setItem(HANDLE_LAST_KEY, String(Date.now()));
}

// -----------------------------------------------------------------------------
// Daily recap — history of roulette runs so the menu can surface yesterday's
// outcome the next time the player opens the app.
const ROULETTE_HISTORY_KEY = "ppe_roulette_history_v1";

export function pushRouletteHistory(entry) {
  try {
    const list = JSON.parse(localStorage.getItem(ROULETTE_HISTORY_KEY) || "[]");
    list.push({ ...entry, date: new Date().toISOString().slice(0, 10), at: Date.now() });
    // keep last 14 days
    const trimmed = list.slice(-14);
    localStorage.setItem(ROULETTE_HISTORY_KEY, JSON.stringify(trimmed));
  } catch {}
}

export function getYesterdayRouletteRecap() {
  try {
    const list = JSON.parse(localStorage.getItem(ROULETTE_HISTORY_KEY) || "[]");
    if (!list.length) return null;
    const now = new Date();
    const yesterday = new Date(now.getTime() - 24 * 3600 * 1000).toISOString().slice(0, 10);
    const today = now.toISOString().slice(0, 10);
    // Show yesterday's entry only until player has played today
    const playedToday = list.some((e) => e.date === today);
    if (playedToday) return null;
    const entry = list.reverse().find((e) => e.date === yesterday);
    return entry || null;
  } catch {
    return null;
  }
}

// -----------------------------------------------------------------------------
// Handle gems (crown/fire/star) — cosmetic prefix on leaderboards
const HANDLE_GEM_KEY = "ppe_handle_gem_v1";
const HANDLE_GEMS_OWNED_KEY = "ppe_handle_gems_owned_v1";

export const HANDLE_GEMS = [
  { id: "crown", symbol: "♛", name: "CROWN", price: 300, color: "#FFD400" },
  { id: "fire", symbol: "▲", name: "FIRE", price: 200, color: "#FF6030" },
  { id: "star", symbol: "★", name: "STAR", price: 150, color: "#F0ABFC" },
];

// Prismatic is trade-only: earned by combining 3 of any single gem.
export const PRISMATIC_GEM = {
  id: "prismatic",
  symbol: "◈",
  name: "PRISMATIC",
  color: "#B0F0FF",
};

export function getHandleGem() {
  return localStorage.getItem(HANDLE_GEM_KEY) || "";
}

export function setHandleGem(id) {
  if (id) localStorage.setItem(HANDLE_GEM_KEY, id);
  else localStorage.removeItem(HANDLE_GEM_KEY);
}

/** Gem inventory: { crown: count, fire: count, star: count, prismatic: count }. */
export function getGemInventory() {
  try {
    const raw = localStorage.getItem(HANDLE_GEMS_OWNED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Backward compat: older versions stored as array of gem ids
    if (Array.isArray(parsed)) {
      const map = {};
      for (const id of parsed) map[id] = (map[id] || 0) + 1;
      localStorage.setItem(HANDLE_GEMS_OWNED_KEY, JSON.stringify(map));
      return map;
    }
    return parsed || {};
  } catch {
    return {};
  }
}

export function getOwnedGems() {
  const inv = getGemInventory();
  return Object.keys(inv).filter((id) => (inv[id] || 0) > 0);
}

export function markGemOwned(id) {
  const inv = getGemInventory();
  inv[id] = (inv[id] || 0) + 1;
  localStorage.setItem(HANDLE_GEMS_OWNED_KEY, JSON.stringify(inv));
}

/** Trade 3× of any base gem for 1× prismatic. Returns {ok, error?} */
export function tradeForPrismatic(baseGemId) {
  const inv = getGemInventory();
  if (!inv[baseGemId] || inv[baseGemId] < 3) {
    return { ok: false, error: "Need 3 copies of the same gem" };
  }
  inv[baseGemId] -= 3;
  if (inv[baseGemId] <= 0) delete inv[baseGemId];
  inv.prismatic = (inv.prismatic || 0) + 1;
  localStorage.setItem(HANDLE_GEMS_OWNED_KEY, JSON.stringify(inv));
  return { ok: true };
}

/** Remove a single gem copy from inventory (returns true if removed). */
export function consumeGem(gemId) {
  const inv = getGemInventory();
  if (!inv[gemId] || inv[gemId] <= 0) return false;
  inv[gemId] -= 1;
  if (inv[gemId] <= 0) delete inv[gemId];
  localStorage.setItem(HANDLE_GEMS_OWNED_KEY, JSON.stringify(inv));
  return true;
}

/** Add a single gem copy to inventory. */
export function addGem(gemId) {
  markGemOwned(gemId);
}

// -----------------------------------------------------------------------------
// Streak Vault — a rare skin awarded automatically for chaining 5 roulette wins
export function shouldAwardStreakVault() {
  return getRouletteStreak() >= 5;
}
